import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
import type { Room } from "../../data/rooms"
import apiClient from "../../api/apiClient"

interface RoomsState {
  rooms: Room[]
  isLoading: boolean
  error: string | null
}

const initialState: RoomsState = {
  rooms: [],
  isLoading: false,
  error: null,
}

// Async thunks
export const fetchRooms = createAsyncThunk(
  "rooms/fetchRooms",
  async ({ building, type }: { building?: string; type?: string } = {}, { rejectWithValue }) => {
    try {
      let url = "/rooms"
      const params = new URLSearchParams()

      if (building && building !== "all") {
        params.append("building", building)
      }

      if (type && type !== "all") {
        params.append("type", type)
      }

      if (params.toString()) {
        url += `?${params.toString()}`
      }

      const response = await apiClient.get(url)
      return response.data.rooms
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch rooms")
    }
  },
)

export const inquireAboutRoom = createAsyncThunk(
  "rooms/inquireAboutRoom",
  async ({ roomId, message }: { roomId: string; message: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/rooms/inquire/${roomId}`, { message })
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to submit inquiry")
    }
  },
)

export const addRoom = createAsyncThunk(
  "rooms/addRoom",
  async (
    roomData: { building: string; number: string; type: string; size: string; price: number; available: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiClient.post("/rooms", roomData)
      return response.data.room
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to add room")
    }
  },
)

export const updateRoom = createAsyncThunk(
  "rooms/updateRoom",
  async (
    {
      roomId,
      ...roomData
    }: {
      roomId: string
      building: string
      number: string
      type: string
      size: string
      price: number
      available: string
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiClient.put(`/rooms/${roomId}`, roomData)
      return { ...roomData, id: roomId }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to update room")
    }
  },
)

export const deleteRoom = createAsyncThunk("rooms/deleteRoom", async (roomId: string, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/rooms/${roomId}`)
    return roomId
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.error || "Failed to delete room")
  }
})

const roomsSlice = createSlice({
  name: "rooms",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch rooms
      .addCase(fetchRooms.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchRooms.fulfilled, (state, action: PayloadAction<Room[]>) => {
        state.rooms = action.payload
        state.isLoading = false
        state.error = null
      })
      .addCase(fetchRooms.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Add room
      .addCase(addRoom.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(addRoom.fulfilled, (state, action: PayloadAction<Room>) => {
        state.rooms.push(action.payload)
        state.isLoading = false
        state.error = null
      })
      .addCase(addRoom.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Update room
      .addCase(updateRoom.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateRoom.fulfilled, (state, action) => {
        const index = state.rooms.findIndex((room) => room.id === action.payload.id)
        if (index !== -1) {
          state.rooms[index] = action.payload as Room
        }
        state.isLoading = false
        state.error = null
      })
      .addCase(updateRoom.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Delete room
      .addCase(deleteRoom.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deleteRoom.fulfilled, (state, action) => {
        state.rooms = state.rooms.filter((room) => room.id !== action.payload)
        state.isLoading = false
        state.error = null
      })
      .addCase(deleteRoom.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export default roomsSlice.reducer
